import { useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button, EmptyState, Field, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useStoreChoice } from '../lib/store-choice';
import type { SalesBreakdown, SalesReport } from '../lib/types-faz8';
import { ColumnChart, StatTile } from '../components/reports/charts';
import { downloadReport } from '../components/reports/download';
import { PeriodControls, usePeriod } from '../components/reports/period';
import { PrintButton, PrintHeader } from '../components/reports/print';

/**
 * Satış raporu (F8-01, ADR-802): dönem özeti ve önceki dönem, net satış grafiği ve dönem tablosu, brütten nete
 * döküm, kırılımlar. Tanımlar: docs/faz-8/REPORTS_FINANCE_TR.md.
 */

const CHANNELS = ['web', 'b2b', 'manual', 'api'] as const;
const SEGMENTS = ['retail', 'dealer'] as const;
const LINE_DIMS = ['product', 'variant', 'category', 'brand'] as const;
const ORDER_DIMS = ['channel', 'store', 'segment', 'paymentMethod', 'country'] as const;
type Dim = (typeof LINE_DIMS)[number] | (typeof ORDER_DIMS)[number];

const th = 'px-3 py-2 text-start font-medium';
const thR = 'px-3 py-2 text-end font-medium';
const td = 'px-3 py-2';
const tdR = 'px-3 py-2 text-end tabular';

const MONTHS_TAG: Record<string, string> = { tr: 'tr-TR', en: 'en-GB', de: 'de-DE', ar: 'ar' };

/** Dönem diliminin ekrandaki adı (gün: 12 Eki · hafta: 12 Eki haftası · ay: Eki 2026 · yıl: 2026). */
function useBucketLabel(interval: string) {
  const { locale } = useI18n();
  const tag = MONTHS_TAG[locale] ?? 'tr-TR';
  return (bucket: string) => {
    const d = new Date(`${bucket}T12:00:00Z`);
    if (interval === 'year') return bucket.slice(0, 4);
    if (interval === 'month')
      return new Intl.DateTimeFormat(tag, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d);
    return new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
  };
}

export function SalesReportPage() {
  const t = useT();
  const money = useMoney();
  const { locale } = useI18n();
  const period = usePeriod();
  const { stores } = useStoreChoice();
  const [storeId, setStoreId] = useState('');
  const [channel, setChannel] = useState('');
  const [segment, setSegment] = useState('');
  const params = new URLSearchParams(period.params);
  if (storeId) params.set('storeId', storeId);
  if (channel) params.set('channel', channel);
  if (segment) params.set('segment', segment);
  const q = useQuery({
    queryKey: ['sales-report', params.toString()],
    queryFn: () => api.get<SalesReport>(`/v1/admin/reports/sales?${params}`),
    placeholderData: keepPreviousData,
  });
  const tag = MONTHS_TAG[locale] ?? 'tr-TR';
  const tl = (n: number | null) => money(n, 'TRY');
  const compact = (minor: number) =>
    new Intl.NumberFormat(tag, {
      style: 'currency',
      currency: 'TRY',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(minor / 100);
  const bucketLabel = useBucketLabel(period.interval);
  const d = q.data;
  return (
    <PageFrame
      helpKey="page.sales_report"
      printable
      actions={
        <span className="inline-flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => void downloadReport(`/v1/admin/reports/sales?${params}&format=csv`, 'satis.csv')}
          >
            {t('admin.reports.csv')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void downloadReport(`/v1/admin/reports/sales?${params}&format=xlsx`, 'satis.xlsx')}
          >
            {t('admin.reports.xlsx')}
          </Button>
          <HelpTip k="reports.download" />
          <PrintButton />
        </span>
      }
    >
      <PrintHeader title={t('admin.salesReport.navLabel')} period={period} />
      <div className="flex flex-wrap items-end gap-4 print:hidden">
        <PeriodControls period={period} />
        {stores.length > 1 && (
          <Field label={t('admin.salesReport.store')} help={<HelpTip k="sales_report.store" />}>
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                <option value="">{t('admin.reports.all')}</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.salesReport.channel')} help={<HelpTip k="sales_report.channel" />}>
          {(p) => (
            <Select {...p} value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">{t('admin.reports.all')}</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.salesReport.channels.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.salesReport.segment')} help={<HelpTip k="sales_report.segment" />}>
          {(p) => (
            <Select {...p} value={segment} onChange={(e) => setSegment(e.target.value)}>
              <option value="">{t('admin.reports.all')}</option>
              {SEGMENTS.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.salesReport.segments.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className={q.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <div className="space-y-1 text-sm text-ink-3">
            <p>{t('admin.reports.currencyNote')}</p>
            {d.period.partial && <p>{t('admin.reports.partial')}</p>}
            {d.awaitingPayment.orders > 0 && (
              <p role="status">
                {t('admin.salesReport.awaiting', {
                  count: d.awaitingPayment.orders,
                  amount: tl(d.awaitingPayment.amountMinor),
                })}
              </p>
            )}
            {d.fxMissing > 0 && (
              <p role="alert" className="text-amber">
                {t('admin.salesReport.fxMissing', { count: d.fxMissing })}
              </p>
            )}
          </div>

          <section
            aria-label={t('admin.salesReport.tilesTitle')}
            className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            <StatTile
              label={t('admin.salesReport.tiles.netSales')}
              value={tl(d.totals.netSales)}
              current={d.totals.netSales}
              previous={d.previous.netSales}
              previousLabel={tl(d.previous.netSales)}
            />
            <StatTile
              label={t('admin.salesReport.tiles.orders')}
              value={String(d.totals.activeOrders)}
              current={d.totals.activeOrders}
              previous={d.previous.activeOrders}
              previousLabel={String(d.previous.activeOrders)}
            />
            <StatTile
              label={t('admin.salesReport.tiles.averageOrder')}
              value={tl(d.totals.averageOrderMinor)}
              current={d.totals.averageOrderMinor ?? 0}
              previous={d.previous.averageOrderMinor ?? 0}
              previousLabel={tl(d.previous.averageOrderMinor)}
            />
            <StatTile
              label={t('admin.salesReport.tiles.deductions')}
              value={tl(d.totals.deductions)}
              current={d.totals.deductions}
              previous={d.previous.deductions}
              previousLabel={tl(d.previous.deductions)}
            />
            <StatTile
              label={t('admin.salesReport.tiles.newCustomers')}
              value={String(d.totals.newCustomers)}
              current={d.totals.newCustomers}
              previous={d.previous.newCustomers}
              previousLabel={String(d.previous.newCustomers)}
              help={<HelpTip k="sales_report.new_customers" />}
            />
          </section>

          <div className="mt-6">
            <ColumnChart
              title={t('admin.salesReport.chartTitle')}
              points={d.series.map((s) => ({
                key: s.bucket,
                label: bucketLabel(s.bucket),
                value: s.netSales,
              }))}
              formatValue={tl}
              formatTick={compact}
              dimmed={q.isPlaceholderData}
            />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Summary d={d} />
            <SeriesTable d={d} label={bucketLabel} />
          </div>

          <Breakdowns d={d} params={params} />
        </div>
      )}
    </PageFrame>
  );
}

function Table({
  caption,
  head,
  children,
  foot,
}: {
  caption: string;
  head: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-surface-2 text-ink-2">{head}</thead>
        <tbody>{children}</tbody>
        {foot && <tfoot className="border-t-2 border-line bg-surface-2 font-medium">{foot}</tfoot>}
      </table>
    </div>
  );
}

const SUMMARY_ROWS = [
  ['productGross', 'money'],
  ['discounts', 'money'],
  ['shipping', 'money'],
  ['orderTotal', 'money'],
  ['cancellations', 'money'],
  ['refunds', 'money'],
  ['deductions', 'money'],
  ['netSales', 'money'],
  ['tax', 'money'],
  ['netSalesExVat', 'money'],
  ['itemsSold', 'int'],
  ['cancelledOrders', 'int'],
  ['customers', 'int'],
  ['returningCustomers', 'int'],
  ['newCustomerNetSales', 'money'],
  ['returningCustomerNetSales', 'money'],
] as const;

function Summary({ d }: { d: SalesReport }) {
  const t = useT();
  const money = useMoney();
  const strong = new Set(['orderTotal', 'netSales', 'netSalesExVat']);
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-1 text-base font-semibold text-ink">
        {t('admin.salesReport.summaryTitle')}
        <HelpTip k="sales_report.summary" />
      </h2>
      <Table
        caption={t('admin.salesReport.summaryTitle')}
        head={
          <tr>
            <th scope="col" className={th}>
              <span className="sr-only">{t('admin.salesReport.columns.name')}</span>
            </th>
            <th scope="col" className={thR}>
              {t('admin.salesReport.thisPeriod')}
            </th>
            <th scope="col" className={thR}>
              {t('admin.salesReport.previousPeriod')}
            </th>
          </tr>
        }
      >
        {SUMMARY_ROWS.map(([k, kind]) => (
          <tr key={k} className={strong.has(k) ? 'border-t border-line font-medium' : 'border-t border-line'}>
            <th scope="row" className={`${td} text-start font-normal`}>
              {t(`admin.salesReport.rows.${k}`)}
            </th>
            <td className={tdR}>{kind === 'money' ? money(d.totals[k], 'TRY') : d.totals[k]}</td>
            <td className={`${tdR} text-ink-3`}>
              {kind === 'money' ? money(d.previous[k], 'TRY') : d.previous[k]}
            </td>
          </tr>
        ))}
      </Table>
      <p className="mt-2 text-xs text-ink-3">{t('admin.salesReport.deductionNote')}</p>
    </section>
  );
}

function SeriesTable({ d, label }: { d: SalesReport; label: (b: string) => string }) {
  const t = useT();
  const money = useMoney();
  const m = (n: number | null) => money(n, 'TRY');
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold text-ink">{t('admin.salesReport.seriesTitle')}</h2>
      {/* Kaydırılan bölge klavyeyle odaklanabilir (WCAG 2.1.1). */}
      <div
        className="max-h-[32rem] overflow-y-auto"
        tabIndex={0}
        role="region"
        aria-label={t('admin.salesReport.seriesTitle')}
      >
        <Table
          caption={t('admin.salesReport.seriesTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.salesReport.columns.bucket')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.salesReport.columns.orders')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.salesReport.columns.netSales')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.salesReport.columns.averageOrder')}
              </th>
            </tr>
          }
          foot={
            <tr>
              <th scope="row" className={`${td} text-start`}>
                {t('admin.salesReport.total')}
              </th>
              <td className={tdR}>{d.totals.activeOrders}</td>
              <td className={tdR}>{m(d.totals.netSales)}</td>
              <td className={tdR}>{m(d.totals.averageOrderMinor)}</td>
            </tr>
          }
        >
          {d.series.map((s) => (
            <tr key={s.bucket} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-normal`}>
                {label(s.bucket)}
              </th>
              <td className={tdR}>{s.orders}</td>
              <td className={tdR}>{m(s.netSales)}</td>
              <td className={tdR}>{m(s.averageOrderMinor)}</td>
            </tr>
          ))}
        </Table>
      </div>
    </section>
  );
}

function Breakdowns({ d, params }: { d: SalesReport; params: URLSearchParams }) {
  const t = useT();
  const money = useMoney();
  const { locale } = useI18n();
  const [dim, setDim] = useState<Dim>('product');
  const lineDim = (LINE_DIMS as readonly string[]).includes(dim);
  const bp = new URLSearchParams(params);
  bp.set('by', dim);
  const q = useQuery({
    queryKey: ['sales-breakdown', bp.toString()],
    queryFn: () => api.get<SalesBreakdown>(`/v1/admin/reports/sales/breakdown?${bp}`),
    enabled: lineDim,
    placeholderData: keepPreviousData,
  });
  const regions = new Intl.DisplayNames([MONTHS_TAG[locale] ?? 'tr-TR'], { type: 'region' });
  const nameOf = (key: string, label: string | null): string => {
    if (dim === 'channel') return t(`admin.salesReport.channels.${key}`);
    if (dim === 'segment') return t(`admin.salesReport.segments.${key}`);
    if (dim === 'paymentMethod')
      return key === 'none' ? t('admin.reports.none') : t(`admin.orders.paymentMethods.${key}`);
    if (dim === 'country') return regions.of(key) ?? key;
    return label ?? t('admin.reports.none');
  };
  const rows: Array<{
    key: string;
    label: string | null;
    sku?: string | null;
    orders: number;
    quantity?: number;
    netSales: number;
    netSalesExVat: number;
    shareBps: number | null;
  }> = lineDim ? (q.data?.rows ?? []) : d.breakdowns[dim as (typeof ORDER_DIMS)[number]];
  const pct = (bps: number | null) =>
    bps === null ? '—' : `%${(bps / 100).toFixed(1).replace('.', locale === 'en' ? '.' : ',')}`;
  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-end gap-4">
        <h2 className="text-base font-semibold text-ink">{t('admin.salesReport.breakdownTitle')}</h2>
        <Field label={t('admin.salesReport.breakdown')} help={<HelpTip k="sales_report.breakdown" />}>
          {(p) => (
            <Select {...p} value={dim} onChange={(e) => setDim(e.target.value as Dim)}>
              {[...LINE_DIMS, ...ORDER_DIMS].map((x) => (
                <option key={x} value={x}>
                  {t(`admin.salesReport.breakdowns.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {lineDim && (
          <span className="inline-flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void downloadReport(`/v1/admin/reports/sales/breakdown?${bp}&format=csv`, `satis-${dim}.csv`)
              }
            >
              {t('admin.reports.csv')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void downloadReport(
                  `/v1/admin/reports/sales/breakdown?${bp}&format=xlsx`,
                  `satis-${dim}.xlsx`,
                )
              }
            >
              {t('admin.reports.xlsx')}
            </Button>
          </span>
        )}
      </div>
      {lineDim && (
        <p className="mb-2 max-w-[80ch] text-xs text-ink-3">
          {t('admin.salesReport.lineNote')} {dim === 'category' ? t('admin.salesReport.categoryNote') : ''}
        </p>
      )}
      {lineDim && q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : lineDim && !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !rows.length ? (
        <EmptyState title={t('admin.reports.empty')} />
      ) : (
        <div className={lineDim && q.isPlaceholderData ? 'opacity-60' : undefined}>
          <Table
            caption={t(`admin.salesReport.breakdowns.${dim}`)}
            head={
              <tr>
                <th scope="col" className={th}>
                  {t(`admin.salesReport.breakdowns.${dim}`)}
                </th>
                <th scope="col" className={thR}>
                  {t('admin.salesReport.columns.orders')}
                </th>
                {lineDim && (
                  <th scope="col" className={thR}>
                    {t('admin.salesReport.columns.quantity')}
                  </th>
                )}
                <th scope="col" className={thR}>
                  {t('admin.salesReport.columns.netSales')}
                </th>
                <th scope="col" className={thR}>
                  {t('admin.salesReport.columns.netSalesExVat')}
                </th>
                <th scope="col" className={thR}>
                  {t('admin.reports.share')}
                </th>
              </tr>
            }
          >
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-normal`}>
                  <span className={r.key === 'none' ? 'text-ink-3' : undefined}>
                    {nameOf(r.key, r.label)}
                  </span>
                  {r.sku && <span className="block text-xs text-ink-3">{r.sku}</span>}
                </th>
                <td className={tdR}>{r.orders}</td>
                {lineDim && <td className={tdR}>{r.quantity}</td>}
                <td className={tdR}>{money(r.netSales, 'TRY')}</td>
                <td className={tdR}>{money(r.netSalesExVat, 'TRY')}</td>
                <td className={tdR}>{pct(r.shareBps)}</td>
              </tr>
            ))}
          </Table>
          {lineDim && q.data && q.data.total > q.data.rows.length && (
            <p className="mt-2 text-xs text-ink-3">
              {t('admin.salesReport.showing', { shown: q.data.rows.length, total: q.data.total })}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
